import React, { useEffect, useRef, useState } from 'react';
import { XMarkIcon, ArrowUpTrayIcon, FolderIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

export interface CourseSessionOption {
  id: number;
  title: string;
  orderIndex?: number;
}

interface CourseMaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  courseId: number;
  sessions?: CourseSessionOption[];
  /** Pre-select buổi học when opening from session context */
  defaultSessionId?: number | null;
}

const CourseMaterialModal: React.FC<CourseMaterialModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  courseId,
  sessions = [],
  defaultSessionId = null,
}) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('REFERENCE');
  const [file, setFile] = useState<File | null>(null);
  const [folderFiles, setFolderFiles] = useState<FileList | null>(null);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);

  const isSampleProject = type === 'SAMPLE_PROJECT';

  useEffect(() => {
    const el = folderInputRef.current;
    if (!el) return;
    el.setAttribute('webkitdirectory', '');
    el.setAttribute('directory', '');
  }, [isOpen, isSampleProject]);

  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setType('REFERENCE');
    setFile(null);
    setFolderFiles(null);
    setZipFile(null);
    setSessionId(defaultSessionId != null ? String(defaultSessionId) : '');
  }, [isOpen, defaultSessionId]);

  if (!isOpen) return null;

  const buildFolderFormData = (files: FileList, formData: FormData) => {
    const paths: string[] = [];
    Array.from(files).forEach((f) => {
      const rel =
        (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
      formData.append('files', f);
      paths.push(rel);
    });
    formData.append('relativePaths', JSON.stringify(paths));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSampleProject) {
      if (!folderFiles?.length && !zipFile) {
        toast.error('Vui lòng chọn thư mục hoặc file ZIP');
        return;
      }
      let resolvedTitle = title.trim();
      if (!resolvedTitle && zipFile) {
        resolvedTitle = zipFile.name.replace(/\.zip$/i, '');
      }
      if (!resolvedTitle && folderFiles?.length) {
        const first = (folderFiles[0] as File & { webkitRelativePath?: string })
          .webkitRelativePath;
        resolvedTitle = first?.split('/')[0] || folderFiles[0].name;
      }
      if (!resolvedTitle) {
        toast.error('Vui lòng nhập tên dự án mẫu');
        return;
      }

      try {
        setIsSaving(true);
        const formData = new FormData();
        formData.append('title', resolvedTitle);
        if (sessionId) formData.append('sessionId', sessionId);
        if (zipFile) {
          formData.append('zip', zipFile);
        } else if (folderFiles) {
          buildFolderFormData(folderFiles, formData);
        }

        const res = await fetch(
          `/api/v1/admin/courses/${courseId}/materials/sample-project`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
            body: formData,
          }
        );
        const data = await res.json();
        if (data.success) {
          toast.success(data.message || 'Thêm dự án mẫu thành công');
          onSaved();
          onClose();
        } else {
          toast.error(data.message || 'Có lỗi xảy ra');
        }
      } catch (error) {
        console.error('Error saving sample project:', error);
        toast.error('Có lỗi xảy ra khi lưu dự án mẫu');
      } finally {
        setIsSaving(false);
      }
      return;
    }

    if (!title.trim()) {
      toast.error('Vui lòng nhập tên tài liệu');
      return;
    }
    if (!file) {
      toast.error('Vui lòng chọn file');
      return;
    }

    try {
      setIsSaving(true);
      const formData = new FormData();
      formData.append('title', title);
      formData.append('type', type);
      formData.append('file', file);
      if (sessionId) {
        formData.append('sessionId', sessionId);
      }

      const res = await fetch(`/api/v1/admin/courses/${courseId}/materials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: formData
      });

      const data = await res.json();

      if (data.success) {
        toast.success('Thêm mới tài liệu thành công');
        onSaved();
        onClose();
      } else {
        toast.error(data.message || 'Có lỗi xảy ra');
      }
    } catch (error) {
      console.error('Error saving material:', error);
      toast.error('Có lỗi xảy ra khi lưu');
    } finally {
      setIsSaving(false);
    }
  };

  const folderLabel = folderFiles?.length
    ? `${(folderFiles[0] as File & { webkitRelativePath?: string }).webkitRelativePath?.split('/')[0] || 'Thư mục'} (${folderFiles.length} file)`
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-m2-surface-container rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">
            Thêm tài liệu mới
          </h2>
          <button onClick={onClose} className="text-m2-on-surface-variant hover:text-m2-on-surface">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSave}>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-m2-on-surface mb-1">
                Tên tài liệu {isSampleProject ? '(tuỳ chọn)' : <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                className="w-full border border-m2-outline rounded-md p-2 focus:ring-m2-primary focus:border-m2-primary"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={isSampleProject ? 'Để trống = tên thư mục/ZIP' : 'Ví dụ: Slide bài 1'}
                required={!isSampleProject}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-m2-on-surface mb-1">
                Loại tài liệu <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border border-m2-outline rounded-md p-2 focus:ring-m2-primary focus:border-m2-primary"
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setFile(null);
                  setFolderFiles(null);
                  setZipFile(null);
                }}
              >
                <option value="SLIDE">Slide (PPTX, HTML)</option>
                <option value="REFERENCE">Tài liệu tham khảo (PDF, Word, ...)</option>
                <option value="OTHER">Khác</option>
                <option value="SAMPLE_PROJECT">Dự án mẫu (thư mục / ZIP)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-m2-on-surface mb-1">
                Gắn theo buổi học
              </label>
              <select
                className="w-full border border-m2-outline rounded-md p-2 focus:ring-m2-primary focus:border-m2-primary"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
              >
                <option value="">Toàn khóa (không gắn buổi cụ thể)</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.orderIndex != null ? `Buổi ${s.orderIndex}: ` : ''}
                    {s.title}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-m2-on-surface-variant">
                Học viên xem recording / chọn buổi học sẽ thấy tài liệu gắn buổi đó.
              </p>
            </div>

            {isSampleProject ? (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-m2-on-surface">
                  Upload dự án mẫu <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => folderInputRef.current?.click()}
                    className="flex flex-col items-center justify-center rounded-md border-2 border-dashed border-m2-outline px-3 py-6 hover:border-m2-primary hover:bg-m2-primary/5"
                  >
                    <FolderIcon className="mb-2 h-8 w-8 text-amber-600" />
                    <span className="text-sm font-medium text-m2-on-surface">Chọn thư mục</span>
                    <span className="mt-1 text-center text-[11px] text-m2-on-surface-variant">
                      {folderLabel || 'webkitdirectory'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => zipInputRef.current?.click()}
                    className="flex flex-col items-center justify-center rounded-md border-2 border-dashed border-m2-outline px-3 py-6 hover:border-m2-primary hover:bg-m2-primary/5"
                  >
                    <ArrowUpTrayIcon className="mb-2 h-8 w-8 text-m2-primary" />
                    <span className="text-sm font-medium text-m2-on-surface">Chọn ZIP</span>
                    <span className="mt-1 text-center text-[11px] text-m2-on-surface-variant">
                      {zipFile ? zipFile.name : 'Tối đa 200MB'}
                    </span>
                  </button>
                </div>
                <input
                  ref={folderInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  onChange={(e) => {
                    setFolderFiles(e.target.files);
                    setZipFile(null);
                    if (e.target.files?.length && !title) {
                      const rel = (e.target.files[0] as File & { webkitRelativePath?: string })
                        .webkitRelativePath;
                      setTitle(rel?.split('/')[0] || '');
                    }
                  }}
                />
                <input
                  ref={zipInputRef}
                  type="file"
                  className="hidden"
                  accept=".zip"
                  onChange={(e) => {
                    const selected = e.target.files?.[0] || null;
                    setZipFile(selected);
                    setFolderFiles(null);
                    if (selected && !title) {
                      setTitle(selected.name.replace(/\.zip$/i, ''));
                    }
                  }}
                />
                <p className="text-xs text-m2-on-surface-variant">
                  Dự án mẫu hiển thị theo cây thư mục cho học viên, không đưa vào Thư viện Skill.
                </p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-m2-on-surface mb-1">
                  File đính kèm <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-m2-outline border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-m2-outline" />
                    <div className="flex text-sm text-m2-on-surface-variant justify-center">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-m2-surface-container rounded-md font-medium text-m2-primary hover:text-m2-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                      >
                        <span>Tải file lên</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept=".pdf,.pptx,.html,.htm,.docx"
                          onChange={(e) => {
                            const selected = e.target.files?.[0] || null
                            setFile(selected)
                            if (selected) {
                              setTitle(selected.name)
                            }
                          }}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-m2-on-surface-variant">
                      {file ? file.name : "PDF, PPTX, HTML, DOCX tối đa 50MB"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t flex justify-end space-x-3 bg-m2-surface-low rounded-b-lg">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-m2-outline rounded-md text-m2-on-surface bg-m2-surface-container hover:bg-m2-surface-low"
              disabled={isSaving}
            >
              Hủy bỏ
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-m2-primary text-m2-on-primary rounded-md hover:bg-eh-forest-shadow disabled:opacity-50"
              disabled={isSaving}
            >
              {isSaving ? 'Đang lưu...' : 'Thêm mới'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseMaterialModal;
